import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useForm, Controller, useWatch } from 'react-hook-form';
import ImageCropModal from '../../../components/ImageCropModal/ImageCropModal';
import { yupResolver } from '@hookform/resolvers/yup';
import type { SubmitHandler } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import TournamentDetailsPreview from './TournamentDetailsPreview';
import TournamentTickerConfigSection from './components/TournamentTickerConfigSection';
import WaiverSettingsSection from './components/WaiverSettingsSection';
import { useDispatch, useSelector } from 'react-redux';
import { saveTournamentDraft } from '../../../redux/slice/createTournamentSlice';
import { useAppSelector } from '../../../redux/hooks';
import { selectSelectedLeagueId } from '../../../redux/slice/selectedLeagueSlice';
import { fetchLeagueById } from '../../myLeagues/api/myLeaguesApi';
import { QUERY_KEY } from '../../../services/queryEndPoint';
import {
  BALL_TYPES,
  type BallType,
  DEFAULT_TOURNAMENT_RULES,
  FIELD_LIMITS,
  getBallTypeLabel,
  type ICreateTournament,
  type ICreateTournamentUI,
  MATCH_TYPES,
  MatchTypeLabel,
  schema,
  TOURNAMENT_CATEGORIES,
  type TournamentRulesState,
} from './types/createtournament';
import type { TickerRowState } from './tournamentsDetails/matches/tickerAutomationConstants';
import { ProfileDatePicker } from '../../profileOnboarding/components/ProfileDatePicker';

export interface TournamentBannerState {
  base64: string;
  fileType: string;
  fileName: string;
  previewUrl: string;
}

/** Same shape as the banner — a cropped image held as base64 until submit. */
export type TournamentLogoState = TournamentBannerState;

type ImageTarget = 'banner' | 'logo';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export interface TournamentDetailsWizardPayload {
  data: ICreateTournamentUI;
  banner: TournamentBannerState | null;
  logo: TournamentLogoState | null;
  tickerRows: TickerRowState[];
  tournamentRules: TournamentRulesState;
}

type TournamentDetailsProps = {
  /** Legacy: embedded preview + internal create call. */
  onTournamentCreatedSuccess?: (tournamentId?: string) => void;
  onTournamentCreatedBackToHome?: () => void;
  /** Wizard mode: when set, "NEXT" calls this instead of the embedded preview. */
  onNext?: (payload: TournamentDetailsWizardPayload) => void;
  initialValues?: Partial<ICreateTournamentUI>;
  initialBanner?: TournamentBannerState | null;
  initialLogo?: TournamentLogoState | null;
  initialTickerRows?: TickerRowState[];
  initialTournamentRules?: TournamentRulesState;
};

const TournamentDetails: React.FC<TournamentDetailsProps> = ({
  onTournamentCreatedSuccess,
  onTournamentCreatedBackToHome,
  onNext,
  initialValues,
  initialBanner,
  initialLogo,
  initialTickerRows,
  initialTournamentRules,
}) => {
  const wizardMode = !!onNext;
  const dispatch = useDispatch();
  const location = useLocation();
  const [tournamentPreview, setTournamentPreview] = useState(false);
  const isEditMode = location?.state?.pathname?.("/create-tournament");

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = useForm<ICreateTournamentUI, any, ICreateTournamentUI>({
    resolver: yupResolver<ICreateTournamentUI, any, ICreateTournamentUI>(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      leagueId: null,
      bannerUri: null,
      canEditTournament: null,
      isRosterLocked: false,
      canEditScoreCard: false,
      isImpactPlayerAllowed: false,
      bonusPointsEnabled: false,
      isStrictModeEnabled: false,
      isTickerAutomationEnabled: false,
      waiverRequired: false,
      waiverId: null,
      tournamentName: '',
      description: '',
      organizerName: '',
      organizerPhoneNumber: '',
      organizerPhoneNumberCountryCode: '',
      organizerEmailId: '',
      startDate: '',
      endDate: '',
      ...initialValues,
    },
  });

  const watchedValues = useWatch({ control }) as ICreateTournamentUI;
  const canPreview = useMemo(() => schema.isValidSync(watchedValues), [watchedValues]);

  useEffect(() => {
    setValue('leagueId', null, { shouldDirty: false });
    setValue('bannerUri', null, { shouldDirty: false });
  }, [setValue]);

  const matchTypeWatch = watch('matchType');
  const ballTypeWatch = watch('ballType');
  const categoryWatch = watch('tournamentCategory');
  const tickerEnabledWatch = watch('isTickerAutomationEnabled');
  const waiverRequiredWatch = watch('waiverRequired');
  const waiverIdWatch = watch('waiverId');
  const maxOversWatch = watch('maxOvers');
  const tournamentNameW = watch('tournamentName') ?? '';
  const organizerNameW = watch('organizerName') ?? '';
  const organizerEmailW = watch('organizerEmailId') ?? '';
  const descriptionW = watch('description') ?? '';

  const overCount = (() => {
    switch (matchTypeWatch) {
      case 'T20': return 20;
      case 'THE_HUNDRED': return 20;
      case 'INDOOR': return 10;
      case 'LIMITED_OVERS':
        return typeof maxOversWatch === 'number' && maxOversWatch > 0 ? maxOversWatch : 20;
      default: return 20;
    }
  })();

  useEffect(() => {
    setTournamentRules((r) => ({ ...r, excludeOvers: [] }));
  }, [matchTypeWatch]);

  const counter = (value: string, max: number) =>
    value.length >= max - 10 ? (
      <span className="ct-char-counter">
        {value.length}/{max}
      </span>
    ) : null;

  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);
  const { data: leagueDetails } = useQuery({
    queryKey: [QUERY_KEY.getLeagueDetails, selectedLeagueId],
    queryFn: () => fetchLeagueById(selectedLeagueId as string),
    enabled: !!selectedLeagueId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!leagueDetails) return;
    const current = watch();
    if (!current.organizerName && leagueDetails.organizerName) {
      setValue('organizerName', leagueDetails.organizerName, { shouldDirty: false });
    }
    if (!current.organizerEmailId && leagueDetails.organizerEmailId) {
      setValue('organizerEmailId', leagueDetails.organizerEmailId, { shouldDirty: false });
    }
    if (!current.organizerPhoneNumber && leagueDetails.organizerPhoneNumber) {
      const cc =
        leagueDetails.countryCode != null
          ? String(leagueDetails.countryCode).replace(/^\+/, '')
          : leagueDetails.organizer?.countryCode != null
            ? String(leagueDetails.organizer.countryCode).replace(/^\+/, '')
            : '91';
      const rawPhone = String(leagueDetails.organizerPhoneNumber).replace(/\D/g, '');
      const combined = rawPhone.startsWith(cc) ? rawPhone : `${cc}${rawPhone}`;
      setValue('organizerPhoneNumber', combined, { shouldDirty: false });
      setValue('organizerPhoneNumberCountryCode', cc, { shouldDirty: false });
    }
  }, [leagueDetails, setValue, watch]);

  const [banner, setBanner] = useState<TournamentBannerState | null>(initialBanner ?? null);
  const [tickerRows, setTickerRows] = useState<TickerRowState[]>(initialTickerRows ?? []);
  const [tournamentRules, setTournamentRules] = useState<TournamentRulesState>(
    initialTournamentRules ?? DEFAULT_TOURNAMENT_RULES,
  );
  const [logo, setLogo] = useState<TournamentLogoState | null>(initialLogo ?? null);
  const bannerInputRef = useRef<HTMLInputElement | null>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropTarget, setCropTarget] = useState<ImageTarget>('banner');
  const [cropSrc, setCropSrc] = useState('');
  const [cropMimeType, setCropMimeType] = useState('image/jpeg');
  const [cropFileName, setCropFileName] = useState('banner');

  const handleImageChange = (target: ImageTarget) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('Image must be 5MB or smaller.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropTarget(target);
      setCropSrc(reader.result as string);
      setCropMimeType(file.type || 'image/jpeg');
      setCropFileName(file.name);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = (croppedFile: File, previewUrl: string) => {
    setCropOpen(false);
    const target = cropTarget;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const commaIdx = result.indexOf(',');
      const base64 = commaIdx >= 0 ? result.slice(commaIdx + 1) : result;
      const ext =
        (croppedFile.type || '').split('/')[1]?.toLowerCase().split(';')[0] ||
        croppedFile.name.split('.').pop()?.toLowerCase() ||
        'png';
      const image = { base64, fileType: ext, fileName: croppedFile.name, previewUrl };
      if (target === 'logo') setLogo(image);
      else setBanner(image);
    };
    reader.readAsDataURL(croppedFile);
  };

  const fc = (hasErr?: boolean) => `form-control ${hasErr ? 'field-error' : ''}`;

  const onSubmitForm: SubmitHandler<ICreateTournamentUI> = (data) => {
    const payloadUI: ICreateTournamentUI & {
      bannerBase64?: string | null;
      bannerFileType?: string | null;
      bannerFileName?: string | null;
      logoBase64?: string | null;
      logoFileType?: string | null;
      logoFileName?: string | null;
    } = {
      ...data,
      leagueId: data.leagueId ?? null,
      bannerUri: data.bannerUri ?? null,
      bannerBase64: banner?.base64 ?? null,
      bannerFileType: banner?.fileType ?? null,
      bannerFileName: banner?.fileName ?? null,
      logoBase64: logo?.base64 ?? null,
      logoFileType: logo?.fileType ?? null,
      logoFileName: logo?.fileName ?? null,
    };

    if (wizardMode) {
      onNext?.({ data: payloadUI, banner, logo, tickerRows, tournamentRules });
      return;
    }

    dispatch(saveTournamentDraft({ ...payloadUI, tournamentRules } as unknown as ICreateTournament));
    setTournamentPreview(true);
  };

  const draft = useSelector((s: any) => s.createTournament.draft);

  useEffect(() => {
    if (isEditMode) {
      if (draft) reset(draft);
    }
    const d = draft as
      | (ICreateTournamentUI & {
          bannerBase64?: string | null;
          bannerFileType?: string | null;
          bannerFileName?: string | null;
          logoBase64?: string | null;
          logoFileType?: string | null;
          logoFileName?: string | null;
        })
      | null;
    if (d?.bannerBase64 && d?.bannerFileType) {
      setBanner({
        base64: d.bannerBase64,
        fileType: d.bannerFileType,
        fileName: d.bannerFileName ?? 'banner',
        previewUrl: `data:${d.bannerFileType};base64,${d.bannerBase64}`,
      });
    }
    if (d?.logoBase64 && d?.logoFileType) {
      setLogo({
        base64: d.logoBase64,
        fileType: d.logoFileType,
        fileName: d.logoFileName ?? 'logo',
        previewUrl: `data:${d.logoFileType};base64,${d.logoBase64}`,
      });
    }
  }, [draft, reset, isEditMode]);

  return (
    <div>
      <ImageCropModal
        open={cropOpen}
        imageSrc={cropSrc}
        aspect={cropTarget === 'logo' ? 1 : 16 / 9}
        mimeType={cropMimeType}
        fileName={cropFileName}
        onCrop={handleCropConfirm}
        onCancel={() => setCropOpen(false)}
      />
      <div className="common-stepper-details-wrap">
        {!tournamentPreview ? (
          <form className="ct-form" onSubmit={handleSubmit(onSubmitForm)}>
            <h2 className="sec-title">Tournament Details</h2>

            <div className="form-control-wrap">
              <div className="form-group">
                <label>
                  Tournament Name <span className="req">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter Tournament Name"
                  maxLength={FIELD_LIMITS.tournamentName}
                  className={fc(!!errors.tournamentName)}
                  {...register('tournamentName')}
                />
                {counter(tournamentNameW, FIELD_LIMITS.tournamentName)}
                {errors.tournamentName && <p className="err-msg">{errors.tournamentName.message}</p>}
              </div>

              <div className="form-group">
                <label>
                  Mobile Number <span className="req">*</span>
                </label>
                <div className="phone">
                  <Controller
                    control={control}
                    name="organizerPhoneNumber"
                    render={({ field }) => (
                      <PhoneInput
                        country="in"
                        value={field.value ?? ''}
                        countryCodeEditable={false}
                        onChange={(value, country: { dialCode?: string }) => {
                          field.onChange(value);
                          setValue('organizerPhoneNumberCountryCode', country?.dialCode || '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                        inputClass={`${fc(!!errors.organizerPhoneNumber)} phone-input-field`}
                        containerClass="phone-input-container"
                        placeholder="Enter contact number"
                        enableSearch
                      />
                    )}
                  />
                  <input type="hidden" {...register('organizerPhoneNumberCountryCode')} />
                </div>
                {(errors.organizerPhoneNumber || errors.organizerPhoneNumberCountryCode) && (
                  <p className="err-msg">
                    {errors.organizerPhoneNumber?.message || errors.organizerPhoneNumberCountryCode?.message}
                  </p>
                )}
              </div>
            </div>

            <div className="form-control-wrap">
              <div className="form-group">
                <label htmlFor="tournament-start-date">
                  Tournaments Date <span className="req">*</span>
                </label>
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field }) => (
                    <ProfileDatePicker
                      id="tournament-start-date"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="Start Date"
                      hasError={!!errors.startDate}
                      disablePast
                    />
                  )}
                />
                {errors.startDate && <p className="err-msg">{errors.startDate.message}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="tournament-end-date">
                  End Date <span className="req">*</span>
                </label>
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field }) => (
                    <ProfileDatePicker
                      id="tournament-end-date"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="End Date"
                      hasError={!!errors.endDate}
                      disablePast
                    />
                  )}
                />
                {errors.endDate && <p className="err-msg">{errors.endDate.message}</p>}
              </div>
            </div>

            <div className="form-control-wrap">
              <div className="form-group">
                <label>
                  Organizer Email <span className="req">*</span>
                </label>
                <input
                  type="email"
                  placeholder="Enter Email id"
                  maxLength={FIELD_LIMITS.organizerEmail}
                  className={fc(!!errors.organizerEmailId)}
                  {...register('organizerEmailId')}
                />
                {counter(organizerEmailW, FIELD_LIMITS.organizerEmail)}
                {errors.organizerEmailId && <p className="err-msg">{errors.organizerEmailId.message}</p>}
              </div>
              <div className="form-group">
                <label>
                  Organizer Name <span className="req">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter Organizer Name"
                  maxLength={FIELD_LIMITS.organizerName}
                  className={fc(!!errors.organizerName)}
                  {...register('organizerName')}
                />
                {counter(organizerNameW, FIELD_LIMITS.organizerName)}
                {errors.organizerName && <p className="err-msg">{errors.organizerName.message}</p>}
              </div>
            </div>

            <hr className="soft-sep" />

            <h2 className="sec-title">Scoring Rules & Settings</h2>

            <div className="score-rule-wrap">
              {matchTypeWatch !== 'TEST' && (
                <div className="over-bolwer">
                  <label>
                    Overs per Bowler <span className="req">*</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Enter overs"
                    className={fc(!!errors.maxOversPerBowler)}
                    {...register('maxOversPerBowler', { valueAsNumber: true })}
                  />
                  {errors.maxOversPerBowler && <p className="err-msg">{errors.maxOversPerBowler.message}</p>}
                </div>
              )}

              <div className="points-data-wrap">
                {matchTypeWatch !== 'TEST' && (
                  <div className="points-data">
                    <label>
                      Points for Win <span className="req">*</span>
                    </label>
                    <input
                      type="number"
                      placeholder="Enter Points"
                      className={fc(!!errors.pointForWin)}
                      {...register('pointForWin', { valueAsNumber: true })}
                    />
                    {errors.pointForWin && <p className="err-msg">{errors.pointForWin.message}</p>}
                  </div>
                )}
                <div className="points-data">
                  <label>
                    Points for Tie <span className="req">*</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Enter Points"
                    className={fc(!!errors.pointForTie)}
                    {...register('pointForTie', { valueAsNumber: true })}
                  />
                  {errors.pointForTie && <p className="err-msg">{errors.pointForTie.message}</p>}
                </div>
                <div className="points-data">
                  <label>
                    Points for Abandoned <span className="req">*</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Enter Points"
                    className={fc(!!errors.pointForAbandoned)}
                    {...register('pointForAbandoned', { valueAsNumber: true })}
                  />
                  {errors.pointForAbandoned && <p className="err-msg">{errors.pointForAbandoned.message}</p>}
                </div>
              </div>
            </div>

            <div className="match-type-wrapper">
              <div className="match-type">
                <label>
                  Match Format <span className="req">*</span>
                </label>
                <div className="chip-group">
                  {MATCH_TYPES.map((mt) => (
                    <button
                      type="button"
                      key={mt}
                      className={`chip ${matchTypeWatch === mt ? 'selected' : ''}`}
                      onClick={() => setValue('matchType', mt, { shouldValidate: true, shouldDirty: true })}
                    >
                      {MatchTypeLabel[mt]}
                    </button>
                  ))}
                </div>
                {errors.matchType && <p className="err-msg">{errors.matchType.message as string}</p>}
              </div>

              <div className="ball-type">
                <label>
                  Ball <span className="req">*</span>
                </label>
                <div className="chip-group">
                  {BALL_TYPES.map((bt) => (
                    <button
                      type="button"
                      key={bt}
                      className={`chip ${ballTypeWatch === bt ? 'selected' : ''}`}
                      onClick={() => setValue('ballType', bt, { shouldValidate: true, shouldDirty: true })}
                    >
                      {getBallTypeLabel(bt as BallType)}
                    </button>
                  ))}
                </div>
                {errors.ballType && <p className="err-msg">{errors.ballType.message as string}</p>}
              </div>
            </div>

            {matchTypeWatch === 'LIMITED_OVERS' && (
              <div className="other-limited-wrap">
                <div className="over-wrap">
                  <label>
                    No. Of Overs <span className="req">*</span>
                  </label>
                  <input
                    className={fc(!!errors.maxOvers)}
                    placeholder="Enter Overs"
                    {...register('maxOvers', { valueAsNumber: true })}
                  />
                  {errors.maxOvers && <p className="err-msg">{errors.maxOvers.message as string}</p>}
                </div>
                <div className="wicket-wrap">
                  <label>
                    No. Of Wickets <span className="req">*</span>
                  </label>
                  <input
                    className={fc(!!errors.maxWickets)}
                    placeholder="Enter wickets"
                    {...register('maxWickets', { valueAsNumber: true })}
                  />
                  {errors.maxWickets && <p className="err-msg">{errors.maxWickets.message as string}</p>}
                </div>
              </div>
            )}

            {matchTypeWatch === 'TEST' && (
              <div style={{ marginTop: '16px' }}>
                <div className="form-control-wrap">
                  <div className="form-group">
                    <label>Points for First Innings Lead</label>
                    <input
                      type="number"
                      placeholder="Enter Points"
                      className={fc(!!errors.pointForFirstInningsLead)}
                      {...register('pointForFirstInningsLead', { valueAsNumber: true })}
                    />
                    {errors.pointForFirstInningsLead && (
                      <p className="err-msg">{errors.pointForFirstInningsLead.message}</p>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Points for First Innings Loss</label>
                    <input
                      type="number"
                      placeholder="Enter Points"
                      className={fc(!!errors.pointForFirstInningsLoss)}
                      {...register('pointForFirstInningsLoss', { valueAsNumber: true })}
                    />
                    {errors.pointForFirstInningsLoss && (
                      <p className="err-msg">{errors.pointForFirstInningsLoss.message}</p>
                    )}
                  </div>
                </div>
                <div className="form-control-wrap">
                  <div className="form-group">
                    <label>Points for Outright Win</label>
                    <input
                      type="number"
                      placeholder="Enter Points"
                      className={fc(!!errors.pointForOutrightWin)}
                      {...register('pointForOutrightWin', { valueAsNumber: true })}
                    />
                    {errors.pointForOutrightWin && (
                      <p className="err-msg">{errors.pointForOutrightWin.message}</p>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Bonus Points for Win by Innings</label>
                    <input
                      type="number"
                      placeholder="Enter Points"
                      className={fc(!!errors.bonusPointForWinByInnings)}
                      {...register('bonusPointForWinByInnings', { valueAsNumber: true })}
                    />
                    {errors.bonusPointForWinByInnings && (
                      <p className="err-msg">{errors.bonusPointForWinByInnings.message}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Wide/No Ball Rules — shown for all match types */}
            <div style={{ marginTop: '16px' }}>
              <hr className="soft-sep" />
              <h2 className="sec-title">Wide/No Ball Rules</h2>

              <div className="tournament-rules-card">
                <div className="tournament-rule-row">
                  <span className="rule-badge">A</span>
                  <span className="rule-label">Count wide as a legal delivery</span>
                  <label className="toggle" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={tournamentRules.countWideBall}
                      onChange={(e) => setTournamentRules((r) => ({ ...r, countWideBall: e.target.checked }))}
                    />
                    <span className="slider" />
                  </label>
                </div>
                <div className="tournament-rule-row">
                  <span className="rule-badge">B</span>
                  <span className="rule-label">Wide</span>
                  <div className="rule-counter">
                    <button
                      type="button"
                      className="counter-btn"
                      onClick={() => setTournamentRules((r) => ({ ...r, runsForWideBall: Math.max(0, r.runsForWideBall - 1) }))}
                    >−</button>
                    <span className="counter-value">{tournamentRules.runsForWideBall}</span>
                    <button
                      type="button"
                      className="counter-btn"
                      onClick={() => setTournamentRules((r) => ({ ...r, runsForWideBall: r.runsForWideBall + 1 }))}
                    >+</button>
                  </div>
                </div>
                <div className="tournament-rule-row">
                  <span className="rule-badge">C</span>
                  <span className="rule-label">Count no ball as a legal delivery</span>
                  <label className="toggle" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={tournamentRules.countNoBall}
                      onChange={(e) => setTournamentRules((r) => ({ ...r, countNoBall: e.target.checked }))}
                    />
                    <span className="slider" />
                  </label>
                </div>
                <div className="tournament-rule-row">
                  <span className="rule-badge">D</span>
                  <span className="rule-label">No Ball</span>
                  <div className="rule-counter">
                    <button
                      type="button"
                      className="counter-btn"
                      onClick={() => setTournamentRules((r) => ({ ...r, runsForNoBall: Math.max(0, r.runsForNoBall - 1) }))}
                    >−</button>
                    <span className="counter-value">{tournamentRules.runsForNoBall}</span>
                    <button
                      type="button"
                      className="counter-btn"
                      onClick={() => setTournamentRules((r) => ({ ...r, runsForNoBall: r.runsForNoBall + 1 }))}
                    >+</button>
                  </div>
                </div>
              </div>

              {matchTypeWatch !== 'TEST' && (
                <div className="tournament-rules-meta">
                  <div className="rules-meta-row">
                    <span className="rules-meta-label">Ignore rules</span>
                    <div className="rules-chips">
                      {['A', 'B', 'C', 'D'].map((rule) => (
                        <button
                          type="button"
                          key={rule}
                          className={`rules-chip ${tournamentRules.excludeRules.includes(rule) ? 'selected' : ''}`}
                          onClick={() =>
                            setTournamentRules((r) => ({
                              ...r,
                              excludeRules: r.excludeRules.includes(rule)
                                ? r.excludeRules.filter((x) => x !== rule)
                                : [...r.excludeRules, rule],
                            }))
                          }
                        >
                          {rule}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="rules-meta-row">
                    <span className="rules-meta-label">For overs</span>
                    <div className="rules-chips overs-chips">
                      {Array.from({ length: overCount }, (_, i) => i + 1).map((over) => (
                        <button
                          type="button"
                          key={over}
                          className={`rules-chip ${tournamentRules.excludeOvers.includes(over) ? 'selected' : ''}`}
                          onClick={() =>
                            setTournamentRules((r) => ({
                              ...r,
                              excludeOvers: r.excludeOvers.includes(over)
                                ? r.excludeOvers.filter((x) => x !== over)
                                : [...r.excludeOvers, over],
                            }))
                          }
                        >
                          {over}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="tournament-category-wrapper">
              <div className="category-wrap">
                <label>
                  Tournament Category <span className="req">*</span>
                </label>
                <div className="chip-group">
                  {TOURNAMENT_CATEGORIES.map((cat) => (
                    <button
                      type="button"
                      key={cat}
                      className={`chip ${categoryWatch === cat ? 'selected' : ''}`}
                      onClick={() =>
                        setValue('tournamentCategory', cat, { shouldValidate: true, shouldDirty: true })
                      }
                    >
                      {cat.charAt(0) + cat.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
                {errors.tournamentCategory && (
                  <p className="err-msg">{errors.tournamentCategory.message as string}</p>
                )}
              </div>
            </div>

            <input type="hidden" {...register('leagueId', { setValueAs: () => null })} />
            <input type="hidden" {...register('bannerUri', { setValueAs: () => null })} />

            <div className="toggles">
              <label className="toggle">
                <span className="label">Lock Roster</span>
                <input type="checkbox" {...register('isRosterLocked')} />
                <span className="slider"></span>
              </label>
              <label className="toggle">
                <span className="label">Allow Other Players to Edit Score</span>
                <input type="checkbox" {...register('canEditScoreCard')} />
                <span className="slider"></span>
              </label>
              <label className="toggle">
                <span className="label">Allow Impact Player</span>
                <input type="checkbox" {...register('isImpactPlayerAllowed')} />
                <span className="slider"></span>
              </label>
              <label className="toggle">
                <span className="label">Enable Bonus Points</span>
                <input type="checkbox" {...register('bonusPointsEnabled')} />
                <span className="slider"></span>
              </label>
              <label className="toggle">
                <span className="label">Strict Mode</span>
                <input type="checkbox" {...register('isStrictModeEnabled')} />
                <span className="slider"></span>
              </label>
              <label className="toggle">
                <span className="label">Enable Ticker Automation</span>
                <input type="checkbox" {...register('isTickerAutomationEnabled')} />
                <span className="slider"></span>
              </label>
            </div>

            {tickerEnabledWatch && (
              <TournamentTickerConfigSection
                initial={tickerRows}
                onChange={setTickerRows}
              />
            )}

            <div className="form-group-textarea">
              <label>
                Tournament Description <span className="req">*</span>
              </label>
              <textarea
                rows={5}
                placeholder="Enter description"
                maxLength={FIELD_LIMITS.description}
                className={`textarea ${errors.description ? 'field-error' : ''}`}
                {...register('description')}
              />
              {counter(descriptionW, FIELD_LIMITS.description)}
              {errors.description && <p className="err-msg">{errors.description.message}</p>}
            </div>

            <div className="form-group-banner">
              <label>Tournament Logo (optional)</label>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageChange('logo')}
              />
              {logo?.previewUrl ? (
                <div className="banner-dropzone banner-dropzone--logo has-image">
                  <img src={logo.previewUrl} alt="Logo preview" className="banner-dropzone-img" />
                  <div className="banner-dropzone-overlay">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      Replace
                    </button>
                    <button type="button" className="btn btn-text" onClick={() => setLogo(null)}>
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="banner-dropzone banner-dropzone--logo empty"
                  onClick={() => logoInputRef.current?.click()}
                >
                  <span className="banner-dropzone-icon" aria-hidden>+</span>
                  <span className="banner-dropzone-title">Upload logo</span>
                  <span className="banner-dropzone-hint">Square, PNG/JPG up to 5MB</span>
                </button>
              )}
            </div>

            <div className="form-group-banner">
              <label>Banner Image (optional)</label>
              <input
                ref={bannerInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageChange('banner')}
              />
              {banner?.previewUrl ? (
                <div className="banner-dropzone has-image">
                  <img
                    src={banner.previewUrl}
                    alt="Banner preview"
                    className="banner-dropzone-img"
                  />
                  <div className="banner-dropzone-overlay">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => bannerInputRef.current?.click()}
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      className="btn btn-text"
                      onClick={() => setBanner(null)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="banner-dropzone empty"
                  onClick={() => bannerInputRef.current?.click()}
                >
                  <span className="banner-dropzone-icon" aria-hidden>+</span>
                  <span className="banner-dropzone-title">Click to upload banner</span>
                  <span className="banner-dropzone-hint">PNG, JPG up to 5MB</span>
                </button>
              )}
            </div>

            <WaiverSettingsSection
              leagueId={selectedLeagueId ?? null}
              waiverRequired={!!waiverRequiredWatch}
              waiverId={waiverIdWatch ?? null}
              onWaiverRequiredChange={(value) =>
                setValue('waiverRequired', value, { shouldValidate: true, shouldDirty: true })
              }
              onWaiverIdChange={(value) =>
                setValue('waiverId', value, { shouldValidate: true, shouldDirty: true })
              }
              error={errors.waiverId?.message as string | undefined}
            />

            <div className="actions">
              <button type="submit" className="btn btn-primary" onClick={() => { if (!canPreview) trigger(); }}>
                {wizardMode ? 'NEXT' : 'PREVIEW'}
              </button>
            </div>
          </form>
        ) : (
          <TournamentDetailsPreview
            onEdit={() => setTournamentPreview(false)}
            onTournamentCreatedSuccess={onTournamentCreatedSuccess}
            onTournamentCreatedBackToHome={onTournamentCreatedBackToHome}
          />
        )}
      </div>
    </div>
  );
};

export default TournamentDetails;
